import React, { useState, useEffect, useRef } from "react";
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  ChatBubbleBottomCenterTextIcon,
  CommandLineIcon,
} from "@heroicons/react/24/outline";
import {
  Github,
  Loader2,
  Square,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  X,
} from "lucide-react";
import {
  useGenerateCollectionsFromSwagger,
  useFetchCollectionFromRepo,
  useImportStatus,
} from "../../../useQuery/useProvideContext";
import { useNotification } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";

const LAST_RUN_STORAGE_KEY = "provideContext:lastRun";

const readStoredRun = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LAST_RUN_STORAGE_KEY));
    if (!parsed || (parsed.runStatus !== "success" && parsed.runStatus !== "error")) return null;
    return {
      runStatus: parsed.runStatus,
      runLabel: parsed.runLabel || "",
      startedAt: parsed.startedAt ? new Date(parsed.startedAt) : null,
      endedAt: parsed.endedAt ? new Date(parsed.endedAt) : null,
    };
  } catch {
    return null;
  }
};

const ProvideContext = () => {
  const [selectedOption, setSelectedOption] = useState("swagger");
  const [selectedFile, setSelectedFile] = useState(null);
  const [value, setValue] = useState("");
  const [repoLink, setRepoLink] = useState("");
  const [gitUsername, setGitUsername] = useState("");
  const [gitToken, setGitToken] = useState("");
  const [generate, setGenerate] = useState(0);

  const [runStatus, setRunStatus] = useState(() => readStoredRun()?.runStatus ?? "idle");
  const [runLabel, setRunLabel] = useState(() => readStoredRun()?.runLabel ?? "");
  const [startedAt, setStartedAt] = useState(() => readStoredRun()?.startedAt ?? null);
  const [endedAt, setEndedAt] = useState(() => readStoredRun()?.endedAt ?? null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [isPopupCollapsed, setIsPopupCollapsed] = useState(false);
  const abortControllerRef = useRef(null);

  const { data: importStatus } = useImportStatus(runStatus === "processing");

  useEffect(() => {
    if (runStatus !== "processing") return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [runStatus]);

  useEffect(() => {
    if (runStatus !== "success" && runStatus !== "error") return;
    try {
      localStorage.setItem(
        LAST_RUN_STORAGE_KEY,
        JSON.stringify({
          runStatus,
          runLabel,
          startedAt: startedAt ? startedAt.toISOString() : null,
          endedAt: endedAt ? endedAt.toISOString() : null,
        }),
      );
    } catch {
      // localStorage may be unavailable (e.g. private browsing) — status just won't persist
    }
  }, [runStatus, runLabel, startedAt, endedAt]);

  const formatElapsed = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes === 0) return `${seconds} second${seconds !== 1 ? "s" : ""}`;
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ${seconds} second${seconds !== 1 ? "s" : ""}`;
  };

  const formatTimestamp = (date) =>
    date
      ? date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "";

  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = {
    swagger: [".json", ".yaml", ".yml"],
    postman: [".json"],
  };

  const queryClient = useQueryClient();
  const notify = useNotification();

  const generateCollectionsMutation = useGenerateCollectionsFromSwagger({
    onSuccess: (data) => {
      notify("Collections generated successfully!", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
      setRunStatus("success");
      setEndedAt(new Date());
      setSelectedFile(null);
      setValue("");
      setRepoLink("");
      setGitUsername("");
      setGitToken("");
    },
    onError: (error) => {
      if (error?.code === "ERR_CANCELED") {
        setRunStatus("idle");
        return;
      }
      setRunStatus("error");
      setEndedAt(new Date());
      notify(`Error generating collections: ${error.response.data.message}`, "error");
    },
  });

  const fetchFromRepoMutation = useFetchCollectionFromRepo({
    onSuccess: (data) => {
      notify("Collections fetched from repository successfully!", "success");
      queryClient.invalidateQueries(["latestAPIResults"]);
      setRunStatus("success");
      setEndedAt(new Date());
      setRepoLink("");
      setGitUsername("");
      setGitToken("");
    },
    onError: (error) => {
      if (error?.code === "ERR_CANCELED") {
        setRunStatus("idle");
        return;
      }
      setRunStatus("error");
      setEndedAt(new Date());
      notify(`Error fetching from repository: ${error.message}`, "error");
    },
  });
  const isFileValid = (file) => {
    if (file.size === 0) {
      notify(
        selectedOption === "postman"
          ? "This file is empty. Please upload a valid Postman collection."
          : "This file is empty. Please upload a valid Swagger/OpenAPI specification.",
        "error",
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      notify("File size exceeds 10 MB limit", "error");
      return false;
    }

    const allowed = ALLOWED_EXTENSIONS[selectedOption] || [];

    if (!allowed.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      notify(`Unsupported file type. Please upload a ${allowed.join(", ")} file`, "error");
      return false;
    }

    return true;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (!isFileValid(file)) {
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setRunStatus("idle");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];

    if (!file || !isFileValid(file)) return;

    setSelectedFile(file);
    setRunStatus("idle");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const dropRef = useRef(null);

  const handleAnalyze = () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setRunStatus("processing");
    setRunLabel(selectedOption === "repo" ? repoLink : selectedFile?.name || "Import");
    setStartedAt(new Date());
    setEndedAt(null);
    setNowTick(Date.now());

    if (selectedOption === "repo" && repoLink && gitUsername && gitToken) {
      const formData = new FormData();
      formData.append("file", selectedFile || new Blob());

      fetchFromRepoMutation.mutate({
        repoLink,
        gitUsername,
        gitToken,
        generate,
        signal: controller.signal,
      });
    } else if ((selectedOption === "swagger" || selectedOption === "postman") && selectedFile) {
      if (!isFileValid(selectedFile)) {
        setRunStatus("idle");
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedFile);

      generateCollectionsMutation.mutate({ file: formData, signal: controller.signal });
    }
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
    setRunStatus("idle");
    setStartedAt(null);
    setEndedAt(null);
  };

  const handleDismiss = () => {
    try {
      localStorage.removeItem(LAST_RUN_STORAGE_KEY);
    } catch {
      // ignore
    }
    setRunStatus("idle");
    setStartedAt(null);
    setEndedAt(null);
  };

  const importOptions = [
    {
      id: "swagger",
      icon: CloudArrowUpIcon,
      label: "Swagger/OpenAPI",
      description: "Upload API specification",
      enabled: true,
    },
    {
      id: "postman",
      icon: CommandLineIcon,
      label: "Postman Collection",
      description: "Import collection JSON",
      enabled: true,
    },
    {
      id: "repo",
      icon: Github,
      label: "GitHub Repository",
      description: "Connect your repository",
      enabled: true,
    },
    {
      id: "prompt",
      icon: ChatBubbleBottomCenterTextIcon,
      label: "Describe API",
      description: "Write requirements",
      enabled: true,
    },
    {
      id: "jira",
      icon: DocumentTextIcon,
      label: "Jira Integration",
      description: "Coming soon",
      enabled: false,
    },
    {
      id: "file",
      icon: DocumentTextIcon,
      label: "File Upload",
      description: "Coming soon",
      enabled: false,
    },
  ];

  const isProcessing = runStatus === "processing";

  const isButtonDisabled =
    !(
      ((selectedOption === "swagger" || selectedOption === "postman") && selectedFile) ||
      (selectedOption === "repo" && repoLink && gitUsername && gitToken)
    ) || isProcessing;

  useEffect(() => {
    const handleGlobalDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const file = e.dataTransfer?.files?.[0];

      if (!file || !isFileValid(file)) return;

      setSelectedFile(file);
    };

    const handleGlobalDragOver = (e) => {
      e.preventDefault();
    };

    window.addEventListener("dragover", handleGlobalDragOver);
    window.addEventListener("drop", handleGlobalDrop);

    return () => {
      window.removeEventListener("dragover", handleGlobalDragOver);
      window.removeEventListener("drop", handleGlobalDrop);
    };
  }, [selectedOption]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header Section */}
        <div className="mb-10">
          <div className="inline-block mb-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-blue-700 tracking-wide">IMPORT API</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Bring Your API to Life
          </h1>

          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            Import API specifications, connect repositories, or describe your requirements. We'll
            generate comprehensive collections in seconds.
          </p>
        </div>

        {/* Import Methods Grid */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Choose Import Method</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {importOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedOption === option.id;

              return (
                <button
                  key={option.id}
                  onClick={() => option.enabled && setSelectedOption(option.id)}
                  disabled={!option.enabled}
                  className={`group relative p-5 rounded-xl border-2 transition-all duration-300 text-left overflow-hidden
                    ${
                      !option.enabled
                        ? "opacity-50 cursor-not-allowed bg-slate-50 border-slate-200"
                        : isSelected
                          ? "border-blue-500 bg-blue-50 shadow-lg shadow-blue-200/50"
                          : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-md hover:shadow-blue-100/30"
                    }`}
                >
                  {/* Background accent */}
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity ${
                      isSelected ? "opacity-10" : ""
                    } bg-gradient-to-br from-blue-400 to-blue-600`}
                  />

                  <div className="relative z-10 flex items-start gap-3">
                    <div
                      className={`p-2.5 rounded-lg transition-all ${
                        isSelected
                          ? "bg-blue-500 text-white"
                          : "bg-slate-100 text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-600"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 text-sm">{option.label}</h3>
                      <p className="text-xs text-slate-500 mt-1">{option.description}</p>
                    </div>

                    {isSelected && (
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Configuration Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Configuration</h2>

          {/* Swagger Upload */}
          {selectedOption === "swagger" && (
            <div
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`border-2 border-dashed rounded-xl transition-all duration-300 p-4 text-center ${
                selectedFile
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50"
              }`}
            >
              <input
                type="file"
                className="hidden"
                id="swagger-file"
                onChange={handleFileSelect}
                accept=".json,.yaml,.yml"
              />
              <label htmlFor="swagger-file" className="cursor-pointer block">
                <div className="flex justify-center mb-2">
                  <div
                    className={`p-2 rounded-full ${selectedFile ? "bg-blue-100" : "bg-slate-100"}`}
                  >
                    <CloudArrowUpIcon
                      className={`w-5 h-5 ${selectedFile ? "text-blue-600" : "text-slate-400"}`}
                    />
                  </div>
                </div>

                {selectedFile ? (
                  <div>
                    <p className="text-xs font-semibold text-slate-900 mb-0.5">✓ File selected</p>
                    <p className="text-xs text-slate-600 break-all line-clamp-1">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Click to choose another</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-slate-900 mb-0.5">
                      Drop your Swagger/OpenAPI file here
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      or click • .json, .yaml, .yml • Max 10 MB
                    </p>
                  </div>
                )}
              </label>
            </div>
          )}

          {/* Postman Upload */}
          {selectedOption === "postman" && (
            <div
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`border-2 border-dashed rounded-xl transition-all duration-300 p-4 text-center ${
                selectedFile
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50"
              }`}
            >
              <input
                type="file"
                className="hidden"
                id="postman-file"
                onChange={handleFileSelect}
                accept=".json"
              />
              <label htmlFor="postman-file" className="cursor-pointer block">
                <div className="flex justify-center mb-2">
                  <div
                    className={`p-2 rounded-full ${selectedFile ? "bg-blue-100" : "bg-slate-100"}`}
                  >
                    <CommandLineIcon
                      className={`w-5 h-5 ${selectedFile ? "text-blue-600" : "text-slate-400"}`}
                    />
                  </div>
                </div>

                {selectedFile ? (
                  <div>
                    <p className="text-xs font-semibold text-slate-900 mb-0.5">
                      ✓ Collection ready
                    </p>
                    <p className="text-xs text-slate-600 break-all line-clamp-1">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Click to choose another</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-semibold text-slate-900 mb-0.5">
                      Drop your Postman Collection here
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      or click • JSON format • Max 10 MB
                    </p>
                  </div>
                )}
              </label>
            </div>
          )}

          {/* Description Textarea */}
          {selectedOption === "prompt" && (
            <div className="border-2 border-slate-200 rounded-xl overflow-hidden bg-white hover:border-blue-300 transition-colors">
              <textarea
                placeholder="Describe your API endpoints, parameters, authentication methods, and any specific requirements..."
                className="w-full p-6 border-0 rounded-xl focus:ring-0 focus:outline-none text-sm text-slate-700 placeholder-slate-400 resize-none bg-white"
                rows={8}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">
                Tip: Be specific about your API structure for better results
              </div>
            </div>
          )}

          {/* GitHub Configuration */}
          {selectedOption === "repo" && (
            <div className="space-y-5 bg-white border border-slate-200 rounded-xl p-6">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Repository URL
                </label>
                <input
                  type="url"
                  placeholder="https://github.com/username/repository"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-900 placeholder-slate-400 transition-all"
                  value={repoLink}
                  onChange={(e) => setRepoLink(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-3">
                    GitHub Username
                  </label>
                  <input
                    type="text"
                    placeholder="your-username"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-900 placeholder-slate-400 transition-all"
                    value={gitUsername}
                    onChange={(e) => setGitUsername(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-3">
                    GitHub Token
                  </label>
                  <input
                    type="password"
                    placeholder="ghp_••••••••••••••••"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-900 placeholder-slate-400 transition-all"
                    value={gitToken}
                    onChange={(e) => setGitToken(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-3 pt-2 border-t border-slate-200 mt-4">
                <input
                  type="checkbox"
                  id="generate"
                  checked={generate === 1}
                  onChange={(e) => setGenerate(e.target.checked ? 1 : 0)}
                  className="w-4 h-4 border-slate-300 rounded cursor-pointer accent-blue-600"
                />
                <label
                  htmlFor="generate"
                  className="text-sm text-slate-700 cursor-pointer font-medium"
                >
                  Generate test cases automatically
                </label>
              </div>
            </div>
          )}

          {/* Placeholder for Coming Soon */}
          {(selectedOption === "jira" || selectedOption === "file") && (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center bg-slate-50">
              <div className="flex justify-center mb-4">
                <div className="p-4 rounded-full bg-slate-100">
                  <DocumentTextIcon className="w-8 h-8 text-slate-400" />
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-600 mb-1">
                {selectedOption === "jira" ? "Jira Integration" : "File Upload"} Coming Soon
              </p>
              <p className="text-xs text-slate-500">
                This feature will be available in a future update
              </p>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="mt-8 pt-6 pb-6 border-t border-slate-200 flex justify-end">
          <button
            onClick={handleAnalyze}
            disabled={isButtonDisabled}
            style={
              !isButtonDisabled ? { background: "linear-gradient(to right, #2563eb, #1d4ed8)" } : {}
            }
            className={`px-8 py-3 text-sm font-semibold rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap
      ${
        !isButtonDisabled
          ? "text-white hover:opacity-90 shadow-lg hover:shadow-xl active:scale-95"
          : "bg-slate-200 text-slate-400 cursor-not-allowed"
      }`}
          >
            {isProcessing ? "Processing..." : "Analyze & Generate Collections"}
          </button>
        </div>
      </div>

      {/* Status Popup */}
      {runStatus !== "idle" && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-w-sm">
            {/* Header */}
            <div
              className={`px-4 py-3 border-b ${
                runStatus === "processing"
                  ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100"
                  : runStatus === "success"
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-100"
                    : "bg-gradient-to-r from-red-50 to-rose-50 border-red-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {runStatus === "processing" && (
                    <Loader2 className="w-[18px] h-[18px] text-blue-500 animate-spin shrink-0" />
                  )}
                  {runStatus === "success" && (
                    <CheckCircle2 className="w-[18px] h-[18px] text-green-600 shrink-0" />
                  )}
                  {runStatus === "error" && (
                    <XCircle className="w-[18px] h-[18px] text-red-600 shrink-0" />
                  )}
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {runStatus === "processing"
                      ? "Importing API"
                      : runStatus === "success"
                        ? "Import Complete"
                        : "Import Failed"}
                  </h3>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setIsPopupCollapsed(!isPopupCollapsed)}
                    className="p-1 hover:bg-black/5 rounded-md transition-colors cursor-pointer"
                    title={isPopupCollapsed ? "Expand" : "Collapse"}
                  >
                    <ChevronLeft
                      size={16}
                      className={`transition-transform ${
                        isPopupCollapsed ? "rotate-90" : "-rotate-90"
                      }`}
                    />
                  </button>
                  {runStatus === "processing" ? (
                    <button
                      onClick={handleStop}
                      className="p-1 hover:bg-red-100 rounded-md transition-colors text-gray-500 hover:text-red-600 cursor-pointer"
                      title="Stop"
                    >
                      <Square size={14} className="fill-current" />
                    </button>
                  ) : (
                    <button
                      onClick={handleDismiss}
                      className="p-1 hover:bg-black/5 rounded-md transition-colors text-gray-500 hover:text-gray-700 cursor-pointer"
                      title="Dismiss"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            {!isPopupCollapsed && (
              <div className="px-4 py-3 space-y-2">
                <p className="text-xs font-mono text-blue-600 bg-blue-50 p-2 rounded break-all">
                  {runLabel}
                </p>

                <div className="text-xs text-gray-500 space-y-0.5">
                  <p>Started {formatTimestamp(startedAt)}</p>
                  {runStatus !== "processing" && endedAt && (
                    <p>Ended {formatTimestamp(endedAt)}</p>
                  )}
                  <p className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {runStatus === "processing"
                      ? startedAt && formatElapsed(nowTick - startedAt.getTime())
                      : endedAt && formatElapsed(endedAt.getTime() - startedAt.getTime())}
                  </p>
                </div>

                {runStatus === "processing" && (
                  <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                    {importStatus?.currentStage || "This may take a few minutes..."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvideContext;
